import Hero from "./components/sections/Hero";
import ExpertiseSection from "./components/sections/ExpertiseSection";
import ExperienceTimeline from "./components/sections/ExperienceTimeline";
import resumeData from "./data/resumeData";
import { FaEnvelope, FaLinkedinIn, FaPhoneAlt, FaMoon, FaSun, FaArrowUp } from "react-icons/fa";
import { FaArrowUpRightFromSquare } from "react-icons/fa6";
import { useState, useEffect } from "react";
import ResumeSection from "./components/sections/ResumeSection";
import AIFocusSection from "./components/sections/AIFocusSection";
import AIDemoSection from "./components/sections/AIDemoSection";
import ProjectsSection from "./components/sections/ProjectsSection";
import "./App.css";

const THEME_STORAGE_KEY = "portfolio-theme";

const navLinks = [
  { href: "#about", label: "About" },
  { href: "#expertise", label: "Expertise" },
  { href: "#experience", label: "Experience" },
  { href: "#projects", label: "Work" },
  { href: "#ai-focus", label: "AI" },
  { href: "#contact", label: "Contact" },
];

const getInitialDarkMode = () => {
  if (typeof window === "undefined") return false;
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "dark") return true;
    if (stored === "light") return false;
  } catch {
    // Ignore storage access errors (private mode, etc.)
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
};

const App = () => {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    document.documentElement.style.colorScheme = darkMode ? "dark" : "light";
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, darkMode ? "dark" : "light");
    } catch {
      // Ignore persistence failures
    }
  }, [darkMode]);

  useEffect(() => {
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const totalScroll = document.documentElement.scrollTop;
        const windowHeight =
          document.documentElement.scrollHeight - document.documentElement.clientHeight;
        const scroll = windowHeight > 0 ? totalScroll / windowHeight : 0;
        setScrollProgress(scroll);
        setShowScrollTop(totalScroll > 300);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleDarkMode = () => {
    setDarkMode((current) => !current);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const initials = resumeData.name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2);

  return (
    <div
      className={`min-h-screen portfolio-app font-sans transition-colors duration-500 ${
        darkMode ? "bg-stone-950 text-white" : "bg-[#f4f1ea] text-stone-950"
      }`}
    >
      <div className="fixed top-0 left-0 z-50 h-1 w-full bg-stone-200 dark:bg-stone-800">
        <div
          className="h-full bg-gradient-to-r from-teal-400 via-amber-400 to-rose-400 transition-all duration-300 ease-out"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      <header
        className={`sticky top-0 z-40 border-b backdrop-blur-xl transition-colors duration-500 ${
          darkMode
            ? "border-stone-800 bg-stone-950/90"
            : "border-stone-950/10 bg-[#f4f1ea]/90"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-6 lg:px-8">
          <a href="#about" className="group flex items-center gap-4">
            <div
              className={`flex h-11 w-11 items-center justify-center rounded-xl font-bold shadow-[6px_6px_0_rgba(20,184,166,0.45)] transition-all duration-300 group-hover:shadow-[8px_8px_0_rgba(20,184,166,0.6)] ${
                darkMode ? "bg-stone-800 text-teal-300" : "bg-stone-950 text-[#f4f1ea]"
              }`}
            >
              {initials}
            </div>
            <div>
              <p
                className={`text-[0.65rem] font-bold uppercase tracking-[0.28em] transition-colors ${
                  darkMode ? "text-teal-400" : "text-teal-700"
                }`}
              >
                Software Engineer
              </p>
              <p
                className={`text-sm font-bold transition-colors ${
                  darkMode ? "text-white" : "text-stone-950"
                }`}
              >
                {resumeData.name}
              </p>
            </div>
          </a>
          <nav className="hidden items-center gap-1 rounded-full p-1 text-sm font-semibold xl:flex">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`rounded-full px-3 py-2 transition-all duration-300 hover:-translate-y-0.5 ${
                  darkMode
                    ? "text-stone-300 hover:bg-stone-800 hover:text-white"
                    : "text-stone-600 hover:bg-stone-950 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleDarkMode}
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 hover:scale-110 ${
                darkMode
                  ? "bg-stone-800 text-amber-400 hover:bg-stone-700"
                  : "bg-stone-950 text-amber-300 hover:bg-stone-800"
              }`}
              aria-label="Toggle dark mode"
              aria-pressed={darkMode}
            >
              {darkMode ? <FaSun size={16} /> : <FaMoon size={16} />}
            </button>
            <a
              href={resumeData.resumePdf}
              download="Denzil_Martin_Resume.pdf"
              className={`download-resume-button hidden items-center gap-2 rounded-full border px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] transition-all duration-300 hover:-translate-y-0.5 sm:inline-flex ${
                darkMode
                  ? "border-teal-500 bg-teal-600 text-white shadow-teal-950/20 hover:bg-teal-500"
                  : "border-white/10 bg-stone-950 text-white shadow-stone-950/15 hover:bg-teal-700"
              }`}
            >
              <FaArrowUpRightFromSquare size={14} />
              Resume
            </a>
            <a
              href={`mailto:${resumeData.contact.email}?subject=Role%20opportunity%20for%20Denzil%20Martin`}
              className={`hidden items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] shadow-lg transition-all duration-300 hover:-translate-y-0.5 sm:inline-flex ${
                darkMode
                  ? "bg-teal-600 text-white shadow-teal-950/20 hover:bg-teal-500"
                  : "bg-stone-950 text-white shadow-stone-950/15 hover:bg-teal-700"
              }`}
            >
              <FaEnvelope size={14} />
              Contact
            </a>
          </div>
        </div>
        <nav
          aria-label="Section navigation"
          className="flex gap-1 overflow-x-auto border-t border-stone-500/20 px-4 py-2 xl:hidden"
        >
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="shrink-0 rounded-full px-3 py-2 text-xs font-bold hover:bg-teal-600 hover:text-white"
            >
              {link.label}
            </a>
          ))}
        </nav>
      </header>

      <main>
        <Hero darkMode={darkMode} />
        <ExpertiseSection darkMode={darkMode} />
        <ExperienceTimeline darkMode={darkMode} />
        <ProjectsSection darkMode={darkMode} />
        <AIFocusSection darkMode={darkMode} />
        <AIDemoSection darkMode={darkMode} />
        <ResumeSection darkMode={darkMode} />
      </main>

      <footer
        id="contact"
        className={`text-white transition-colors duration-500 ${
          darkMode ? "bg-stone-900" : "bg-stone-950"
        }`}
      >
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-6 md:grid-cols-[1fr_0.85fr] lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-teal-300">
              Let&apos;s Connect
            </p>
            <h3 className="mt-4 max-w-2xl text-4xl font-black leading-tight text-white sm:text-5xl">
              Let&apos;s discuss platform delivery and applied AI.
            </h3>
            <p className="mt-5 max-w-xl text-base leading-7 text-stone-300">
              {resumeData.openTo}
            </p>
          </div>
          <div className="grid gap-4">
            <a
              href={`mailto:${resumeData.contact.email}?subject=Role%20opportunity%20for%20Denzil%20Martin`}
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4 text-sm text-stone-200 transition-all duration-300 hover:scale-[1.02] hover:border-teal-300/60 hover:bg-white/[0.1]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-300 text-stone-950">
                <FaEnvelope size={18} />
              </span>
              <span className="flex-1">
                <span className="block text-xs font-bold uppercase tracking-wider text-stone-400">
                  Email
                </span>
                {resumeData.contact.email}
              </span>
              <FaArrowUpRightFromSquare className="text-stone-500 transition group-hover:text-teal-300" />
            </a>
            {resumeData.contact.phone && (
              <a
                href={`tel:${resumeData.contact.phone.replace(/\s+/g, "")}`}
                className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4 text-sm text-stone-200 transition-all duration-300 hover:scale-[1.02] hover:border-teal-300/60 hover:bg-white/[0.1]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-300 text-stone-950">
                  <FaPhoneAlt size={18} />
                </span>
                <span className="flex-1">{resumeData.contact.phone}</span>
                <FaArrowUpRightFromSquare className="text-stone-500 transition group-hover:text-teal-300" />
              </a>
            )}
            <a
              href={resumeData.contact.linkedin}
              target="_blank"
              rel="noreferrer"
              className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4 text-sm text-stone-200 transition-all duration-300 hover:scale-[1.02] hover:border-teal-300/60 hover:bg-white/[0.1]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-300 text-stone-950">
                <FaLinkedinIn size={18} />
              </span>
              <span className="flex-1">
                <span className="block text-xs font-bold uppercase tracking-wider text-stone-400">
                  LinkedIn
                </span>
                {resumeData.contact.linkedinLabel || resumeData.contact.linkedin}
              </span>
              <FaArrowUpRightFromSquare className="text-stone-500 transition group-hover:text-teal-300" />
            </a>
          </div>
        </div>
        <div className="border-t border-white/10 py-6">
          <p className="text-center text-xs text-stone-500">
            {"Copyright " + new Date().getFullYear() + " " + resumeData.name + ". Built in Munich."}
          </p>
        </div>
      </footer>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`scroll-to-top-button fixed bottom-8 right-8 z-50 flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${
            darkMode
              ? "bg-teal-600 text-white shadow-teal-950/30 hover:bg-teal-500"
              : "bg-stone-950 text-white shadow-stone-950/20 hover:bg-teal-700"
          }`}
          aria-label="Scroll to top"
        >
          <FaArrowUp size={16} />
        </button>
      )}
    </div>
  );
};

export default App;
